/**
 * Per-site Audit Configuration (`/settings/sites`).
 *
 * Lives under Settings → Workspace → Sites. Site is read from
 * `useActiveSiteUuid()` (the chrome's active-site context), not from
 * the URL — switch sites via the settings breadcrumb's site pill.
 *
 * The settings layout (`_app/settings.tsx`) owns the <Island> + nav,
 * so this route renders its body straight into the outlet.
 *
 * The home for everything that used to live on the standalone
 * `/monitors` surface. audit-config-on-sites collapsed that table
 * into per-site columns; this page is the dashboard surface for
 * editing them.
 *
 * Sections (spec §7):
 *   1. Status strip — Active / Paused / Ad-hoc only, schedule label,
 *      last-run timestamp, [Run audits now], [Pause / Resume].
 *   2. Coverage — region multi-select + device multi-select.
 *   3. Schedule — toggle + cron presets (hourly, daily, weekly, custom).
 *   4. Notifications (collapsed) — emails, Slack, webhook override.
 *
 * Saves are explicit ([Save changes]) rather than per-field auto-save
 * — the per-field auto-save the spec mentions is a follow-up. The
 * working form lives in component state and `useUpdateSiteAuditConfig`
 * fires the PATCH; on success react-query lands the fresh `Site`
 * shape in the cache so the status strip re-renders.
 */
import { ConfirmDialog } from '@/components/primitives/ConfirmDialog';
import { ErrorState } from '@/components/primitives/ErrorState';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Field, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useActiveSiteUuid } from '@/lib/active-site';
import type { PatchAuditConfig, Region } from '@/lib/api-types';
import { toastFromError, toastSuccess } from '@/lib/errors';
import { hostOf, humanizeCron, relativeTime } from '@/lib/format';
import {
  useAuditSite,
  useOrgDeviceProfiles,
  useRegions,
  useSite,
  useUpdateSiteAuditConfig,
} from '@/lib/queries';
import { cn } from '@/lib/utils';
import { CaretDown, Check, FloppyDisk, Pause, Play } from '@phosphor-icons/react';
import { createFileRoute } from '@tanstack/react-router';
import { type ReactElement, useEffect, useMemo, useState } from 'react';

export const Route = createFileRoute('/_app/settings/sites')({
  component: SiteSettingsPage,
});

type CronPreset = 'hourly' | 'daily' | 'weekly' | 'custom';

const CRON_PRESETS: Record<Exclude<CronPreset, 'custom'>, string> = {
  hourly: '0 * * * *',
  daily: '0 9 * * *', // 09:00 UTC
  weekly: '0 9 * * 1', // Mon 09:00 UTC
};

function classifyCron(cron: string | null): CronPreset {
  if (cron == null) return 'daily';
  if (cron === CRON_PRESETS.hourly) return 'hourly';
  if (cron === CRON_PRESETS.daily) return 'daily';
  if (cron === CRON_PRESETS.weekly) return 'weekly';
  return 'custom';
}

function SiteSettingsPage(): ReactElement {
  const siteUuid = useActiveSiteUuid();
  const site = useSite(siteUuid);
  const regions = useRegions();
  const deviceProfiles = useOrgDeviceProfiles();
  const update = useUpdateSiteAuditConfig();
  const runAudits = useAuditSite();

  // Working form state — seeded from the site row, then drifts until
  // [Save changes] commits the diff.
  const audit = site.data?.audit;
  const [regionUuids, setRegionUuids] = useState<string[]>([]);
  const [deviceUuids, setDeviceUuids] = useState<string[]>([]);
  const [scheduleOn, setScheduleOn] = useState(false);
  const [preset, setPreset] = useState<CronPreset>('daily');
  const [customCron, setCustomCron] = useState('');
  const [paused, setPaused] = useState(false);
  const [emails, setEmails] = useState('');
  const [slackChannel, setSlackChannel] = useState('');
  const [slackWebhook, setSlackWebhook] = useState('');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [confirmRun, setConfirmRun] = useState(false);

  // Re-seed whenever the site row resolves (or changes after a save).
  // The shape lives on `site.data` so we wait for `audit` to land.
  useEffect(() => {
    if (!audit) return;
    setRegionUuids(audit.regions.map((r) => r.uuid));
    setDeviceUuids(audit.devices.map((d) => d.uuid));
    setScheduleOn(audit.schedule !== null);
    const p = classifyCron(audit.schedule);
    setPreset(p);
    setCustomCron(p === 'custom' && audit.schedule ? audit.schedule : '');
    setPaused(audit.paused_at !== null);
    setEmails(audit.notification_email.join(', '));
    setSlackChannel(audit.notification_slack?.channel ?? '');
    setSlackWebhook(audit.notification_slack?.webhook_url ?? '');
    setWebhookUrl(audit.webhook_url ?? '');
  }, [audit]);

  const gridEmpty = regionUuids.length === 0 || deviceUuids.length === 0;
  const cron = scheduleOn
    ? preset === 'custom'
      ? customCron.trim() || null
      : CRON_PRESETS[preset]
    : null;
  // A schedule over an empty grid is the one combination the API
  // rejects (INVALID_CONFIG) — surface it before the round-trip
  // instead of letting Save 400 into a raw error-code toast.
  const scheduleNeedsGrid = scheduleOn && gridEmpty;

  async function onSave(): Promise<void> {
    const parsedEmails = emails
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    const slack =
      slackChannel.trim() || slackWebhook.trim()
        ? {
            ...(slackChannel.trim() ? { channel: slackChannel.trim() } : {}),
            ...(slackWebhook.trim() ? { webhook_url: slackWebhook.trim() } : {}),
          }
        : null;
    const body: PatchAuditConfig = {
      regions: regionUuids,
      devices: deviceUuids,
      schedule: cron,
      // Pause is deliberately NOT part of Save: the status strip's
      // Pause/Resume button is the single pause control and PATCHes
      // `paused_at` immediately. (Two controls for one state — a switch
      // here applied-on-save plus the instant strip button — was
      // confusing duplication.)
      notification_email: parsedEmails,
      notification_slack: slack,
      webhook_url: webhookUrl.trim() || null,
    };
    if (!siteUuid) return;
    try {
      await update.mutateAsync({ uuid: siteUuid, body });
      toastSuccess('Audit configuration saved', site.data?.name ?? undefined);
    } catch (err) {
      toastFromError(err);
    }
  }

  async function onRunNow(): Promise<void> {
    if (!siteUuid) return;
    try {
      const res = await runAudits.mutateAsync({ siteUuid });
      toastSuccess(
        'Audits dispatched',
        `${res.count} combination${res.count === 1 ? '' : 's'} queued.`,
      );
    } catch (err) {
      toastFromError(err);
    } finally {
      setConfirmRun(false);
    }
  }

  // No active site → nothing to configure against. This shouldn't
  // happen in practice (the breadcrumb's site pill always has a
  // selection) but a deep link could hit it. Surface a soft prompt
  // rather than crashing.
  if (!siteUuid) {
    return (
      <ErrorState
        title="No site selected"
        error={new Error('Pick a site from the switcher to configure audits.')}
        retry={undefined}
      />
    );
  }

  return (
    <>
      <div className="space-y-6">
        {site.isError && (
          <ErrorState
            title="Couldn't load this site"
            error={site.error}
            retry={() => {
              void site.refetch();
            }}
          />
        )}

        {site.isLoading && (
          <div className="space-y-3">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-48 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        )}

        {audit && (
          <>
            <StatusStrip
              audit={audit}
              gridEmpty={gridEmpty}
              onRunNow={() => setConfirmRun(true)}
              onTogglePause={async () => {
                try {
                  await update.mutateAsync({
                    uuid: siteUuid,
                    body: { paused_at: paused ? null : new Date().toISOString() },
                  });
                  setPaused(!paused);
                  toastSuccess(paused ? 'Audits resumed' : 'Audits paused');
                } catch (err) {
                  toastFromError(err);
                }
              }}
              runPending={runAudits.isPending}
            />

            <CoverageSection
              regions={regions.data ?? []}
              regionsLoading={regions.isLoading}
              selectedRegions={regionUuids}
              onRegionsChange={setRegionUuids}
              deviceProfiles={(deviceProfiles.data ?? []).map((d) => ({
                uuid: d.uuid,
                label: d.description,
              }))}
              devicesLoading={deviceProfiles.isLoading}
              selectedDevices={deviceUuids}
              onDevicesChange={setDeviceUuids}
            />

            <ScheduleSection
              scheduleOn={scheduleOn}
              onScheduleOnChange={setScheduleOn}
              preset={preset}
              onPresetChange={setPreset}
              customCron={customCron}
              onCustomCronChange={setCustomCron}
              paused={paused}
              onResume={async () => {
                try {
                  await update.mutateAsync({
                    uuid: siteUuid,
                    body: { paused_at: null },
                  });
                  setPaused(false);
                  toastSuccess('Audits resumed');
                } catch (err) {
                  toastFromError(err);
                }
              }}
            />

            <NotificationsSection
              emails={emails}
              onEmailsChange={setEmails}
              slackChannel={slackChannel}
              onSlackChannelChange={setSlackChannel}
              slackWebhook={slackWebhook}
              onSlackWebhookChange={setSlackWebhook}
              webhookUrl={webhookUrl}
              onWebhookUrlChange={setWebhookUrl}
            />

            <footer className="flex items-center justify-end gap-3">
              {scheduleNeedsGrid && (
                <p className="text-sm text-[var(--color-mute)]">
                  Pick at least one region and one device to run on a schedule — or turn the
                  schedule off.
                </p>
              )}
              <Button
                onClick={() => {
                  void onSave();
                }}
                loading={update.isPending}
                disabled={scheduleNeedsGrid}
              >
                <FloppyDisk aria-hidden="true" />
                Save changes
              </Button>
            </footer>
          </>
        )}
      </div>

      <ConfirmDialog
        open={confirmRun}
        onOpenChange={setConfirmRun}
        title="Run all configured audits now?"
        description={
          <>
            Dispatches one audit for every combination in the grid (regions × devices × enabled
            pages). Billed at on-demand rates if you're above the included allowance.
          </>
        }
        confirmLabel="Run audits"
        onConfirm={onRunNow}
        loading={runAudits.isPending}
      />
    </>
  );
}

// ─── Status strip ────────────────────────────────────────────────

function StatusStrip({
  audit,
  gridEmpty,
  onRunNow,
  onTogglePause,
  runPending,
}: {
  audit: NonNullable<ReturnType<typeof useSite>['data']>['audit'];
  gridEmpty: boolean;
  onRunNow: () => void;
  onTogglePause: () => void | Promise<void>;
  runPending: boolean;
}): ReactElement {
  const scheduled = audit.schedule !== null;
  const paused = audit.paused_at !== null;
  const adHocOnly = !scheduled;
  const statusLabel = adHocOnly ? 'Ad-hoc only' : paused ? 'Paused' : 'Active';
  const statusTone = adHocOnly
    ? 'bg-[var(--color-paper-alt)] text-[var(--color-mute)]'
    : paused
      ? 'bg-[var(--color-warning)]/15 text-[var(--color-warning)]'
      : 'bg-[var(--color-success)]/15 text-[var(--color-success)]';

  return (
    <section className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[var(--color-line)] bg-[var(--color-paper)] px-4 py-3">
      <div className="flex flex-wrap items-center gap-3 text-sm text-[var(--color-ink-soft)]">
        <span
          className={cn(
            'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
            statusTone,
          )}
        >
          {statusLabel}
        </span>
        {scheduled && audit.schedule && (
          <>
            <span aria-hidden="true">·</span>
            {humanizeCron(audit.schedule) ? (
              <span className="text-xs">{humanizeCron(audit.schedule)}</span>
            ) : (
              <span className="font-mono text-xs">{audit.schedule}</span>
            )}
          </>
        )}
        {audit.last_run_at && (
          <>
            <span aria-hidden="true">·</span>
            <span className="text-xs">Last run {relativeTime(audit.last_run_at)}</span>
          </>
        )}
        {audit.grid_size > 0 && (
          <>
            <span aria-hidden="true">·</span>
            <span className="text-xs">
              {audit.grid_size} audit{audit.grid_size === 1 ? '' : 's'} per run
            </span>
          </>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={gridEmpty}
          title={
            gridEmpty ? 'Pick at least one region and one device before running audits.' : undefined
          }
          loading={runPending}
          onClick={onRunNow}
        >
          <Play aria-hidden="true" weight="fill" />
          Run audits now
        </Button>
        {scheduled && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              void onTogglePause();
            }}
          >
            {paused ? (
              <>
                <Play aria-hidden="true" />
                Resume
              </>
            ) : (
              <>
                <Pause aria-hidden="true" />
                Pause
              </>
            )}
          </Button>
        )}
      </div>
    </section>
  );
}

// ─── Coverage section ────────────────────────────────────────────

function CoverageSection({
  regions,
  regionsLoading,
  selectedRegions,
  onRegionsChange,
  deviceProfiles,
  devicesLoading,
  selectedDevices,
  onDevicesChange,
}: {
  regions: Region[];
  regionsLoading: boolean;
  selectedRegions: string[];
  onRegionsChange: (next: string[]) => void;
  deviceProfiles: Array<{ uuid: string; label: string }>;
  devicesLoading: boolean;
  selectedDevices: string[];
  onDevicesChange: (next: string[]) => void;
}): ReactElement {
  const enabledRegions = regions.filter((r) => r.enabled);

  function toggleRegion(uuid: string) {
    onRegionsChange(
      selectedRegions.includes(uuid)
        ? selectedRegions.filter((u) => u !== uuid)
        : [...selectedRegions, uuid],
    );
  }
  function toggleDevice(uuid: string) {
    onDevicesChange(
      selectedDevices.includes(uuid)
        ? selectedDevices.filter((u) => u !== uuid)
        : [...selectedDevices, uuid],
    );
  }

  return (
    <section className="space-y-5 rounded-2xl border border-[var(--color-line)] bg-[var(--color-paper)] p-5">
      <header className="space-y-1">
        <h2 className="text-base font-semibold text-[var(--color-ink)]">Coverage</h2>
        <p className="text-sm text-[var(--color-mute)]">
          Each scheduled run audits every combination of region × device × enabled page.
        </p>
      </header>

      <div className="space-y-2">
        <h3 className="text-sm font-medium text-[var(--color-ink)]">
          Regions{' '}
          <span className="font-normal text-[var(--color-mute)]">
            ({selectedRegions.length} selected)
          </span>
        </h3>
        {regionsLoading ? (
          <Skeleton className="h-16 w-full" />
        ) : enabledRegions.length === 0 ? (
          <p className="text-sm text-[var(--color-mute)]">No regions available.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {enabledRegions.map((r) => {
              const checked = selectedRegions.includes(r.uuid);
              return (
                <TogglePill
                  key={r.uuid}
                  checked={checked}
                  label={r.name}
                  onToggle={() => toggleRegion(r.uuid)}
                />
              );
            })}
          </div>
        )}
      </div>

      <div className="space-y-2">
        <h3 className="text-sm font-medium text-[var(--color-ink)]">
          Device profiles{' '}
          <span className="font-normal text-[var(--color-mute)]">
            ({selectedDevices.length} selected)
          </span>
        </h3>
        {devicesLoading ? (
          <Skeleton className="h-16 w-full" />
        ) : deviceProfiles.length === 0 ? (
          <p className="text-sm text-[var(--color-mute)]">No device profiles available.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {deviceProfiles.map((d) => {
              const checked = selectedDevices.includes(d.uuid);
              return (
                <TogglePill
                  key={d.uuid}
                  checked={checked}
                  label={d.label}
                  onToggle={() => toggleDevice(d.uuid)}
                />
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

/** Appearance-style multi-select pill: content-sized, selection marked by
 *  the soft putty fill + a small check glyph — no boxed checkbox. */
function TogglePill({
  checked,
  label,
  onToggle,
}: {
  checked: boolean;
  label: string;
  onToggle: () => void;
}): ReactElement {
  return (
    <button
      type="button"
      aria-pressed={checked}
      onClick={onToggle}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm outline-none',
        'transition-[border-color,background-color,transform] duration-150 ease-out active:scale-[0.98]',
        'focus-visible:ring-2 focus-visible:ring-[var(--color-accent-brand)]/40',
        checked
          ? 'border-[var(--color-line-strong)] bg-[var(--color-paper-alt)] text-[var(--color-ink)]'
          : 'border-[var(--color-line)] text-[var(--color-mute)] hover:border-[var(--color-line-strong)] hover:text-[var(--color-ink)]',
      )}
    >
      <Check
        aria-hidden="true"
        size={13}
        weight="bold"
        className={cn(
          'shrink-0 transition-opacity duration-150',
          checked ? 'opacity-100' : 'opacity-0 -ml-4',
        )}
      />
      {label}
    </button>
  );
}

// ─── Schedule section ────────────────────────────────────────────

function ScheduleSection({
  scheduleOn,
  onScheduleOnChange,
  preset,
  onPresetChange,
  customCron,
  onCustomCronChange,
  paused,
  onResume,
}: {
  scheduleOn: boolean;
  onScheduleOnChange: (next: boolean) => void;
  preset: CronPreset;
  onPresetChange: (next: CronPreset) => void;
  customCron: string;
  onCustomCronChange: (next: string) => void;
  /** Site-level pause state — a paused site's schedule never fires, so
   *  the card says so instead of looking armed. */
  paused: boolean;
  onResume: () => void | Promise<void>;
}): ReactElement {
  const presetOptions: Array<{ id: CronPreset; label: string; hint: string }> = [
    { id: 'hourly', label: 'Hourly', hint: 'Every hour on the hour.' },
    { id: 'daily', label: 'Daily', hint: 'Once a day at 09:00 UTC.' },
    { id: 'weekly', label: 'Weekly', hint: 'Monday 09:00 UTC.' },
    { id: 'custom', label: 'Custom', hint: 'Provide a cron expression.' },
  ];

  return (
    <section className="space-y-4 rounded-2xl border border-[var(--color-line)] bg-[var(--color-paper)] p-5">
      <header className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <h2 className="text-base font-semibold text-[var(--color-ink)]">Schedule</h2>
          <p className="text-sm text-[var(--color-mute)]">
            When the configured grid should run on its own. Off = ad-hoc only.
          </p>
        </div>
        <label htmlFor="audit-schedule-toggle" className="flex items-center gap-2 text-sm">
          <Switch
            id="audit-schedule-toggle"
            checked={scheduleOn}
            onCheckedChange={onScheduleOnChange}
          />
          <span className="text-[var(--color-ink-soft)]">Run on a schedule</span>
        </label>
      </header>

      {scheduleOn && paused && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-[var(--color-warning)]/40 bg-[var(--color-warning)]/10 px-3 py-2">
          <p className="text-sm text-[var(--color-ink-soft)]">
            Audits are paused — this schedule won't run until you resume.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              void onResume();
            }}
          >
            <Play aria-hidden="true" />
            Resume
          </Button>
        </div>
      )}

      {scheduleOn && (
        <div className="space-y-3">
          <fieldset className="space-y-2">
            <legend className="sr-only">Schedule preset</legend>
            {presetOptions.map((o) => {
              const active = o.id === preset;
              return (
                <label
                  key={o.id}
                  className={cn(
                    'flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2 transition-colors',
                    active
                      ? 'border-[var(--color-line-strong)] bg-[var(--color-paper-alt)]'
                      : 'border-[var(--color-line)] hover:border-[var(--color-line-strong)] hover:bg-[var(--color-paper-alt)]/40',
                  )}
                >
                  <input
                    type="radio"
                    name="schedule-preset"
                    value={o.id}
                    checked={active}
                    onChange={() => onPresetChange(o.id)}
                    className="mt-1 size-4 accent-[var(--color-ink)]"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-[var(--color-ink)]">{o.label}</div>
                    <div className="text-xs text-[var(--color-mute)]">{o.hint}</div>
                  </div>
                </label>
              );
            })}
          </fieldset>

          {preset === 'custom' && (
            <Field>
              <FieldLabel htmlFor="custom-cron">Cron expression</FieldLabel>
              <Input
                id="custom-cron"
                value={customCron}
                onChange={(e) => onCustomCronChange(e.target.value)}
                placeholder="0 */6 * * *"
                className="font-mono"
              />
              <p className="text-xs text-[var(--color-mute)]">
                5-field cron, UTC. The API normalises common shorthands.
              </p>
            </Field>
          )}
        </div>
      )}
    </section>
  );
}

// ─── Notifications section (collapsed by default) ────────────────

function NotificationsSection({
  emails,
  onEmailsChange,
  slackChannel,
  onSlackChannelChange,
  slackWebhook,
  onSlackWebhookChange,
  webhookUrl,
  onWebhookUrlChange,
}: {
  emails: string;
  onEmailsChange: (next: string) => void;
  slackChannel: string;
  onSlackChannelChange: (next: string) => void;
  slackWebhook: string;
  onSlackWebhookChange: (next: string) => void;
  webhookUrl: string;
  onWebhookUrlChange: (next: string) => void;
}): ReactElement {
  const [open, setOpen] = useState(false);
  return (
    <section className="rounded-2xl border border-[var(--color-line)] bg-[var(--color-paper)]">
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger
          render={
            <button
              type="button"
              className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left"
            >
              <div className="space-y-1">
                <h2 className="text-base font-semibold text-[var(--color-ink)]">Notifications</h2>
                <p className="text-sm text-[var(--color-mute)]">
                  Per-site overrides for workspace alert defaults.
                </p>
              </div>
              <CaretDown
                aria-hidden="true"
                className={cn('shrink-0 transition-transform', open ? 'rotate-180' : 'rotate-0')}
              />
            </button>
          }
        />
        <CollapsibleContent>
          <div className="space-y-4 border-t border-[var(--color-line-soft)] px-5 py-4">
            <Field>
              <FieldLabel htmlFor="notif-emails">Email addresses</FieldLabel>
              <Textarea
                id="notif-emails"
                value={emails}
                onChange={(e) => onEmailsChange(e.target.value)}
                placeholder="alice@example.com, bob@example.com"
                rows={2}
              />
              <p className="text-xs text-[var(--color-mute)]">Comma-separated list.</p>
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field>
                <FieldLabel htmlFor="notif-slack-channel">Slack channel</FieldLabel>
                <Input
                  id="notif-slack-channel"
                  value={slackChannel}
                  onChange={(e) => onSlackChannelChange(e.target.value)}
                  placeholder="#alerts"
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="notif-slack-webhook">Slack webhook URL</FieldLabel>
                <Input
                  id="notif-slack-webhook"
                  type="url"
                  value={slackWebhook}
                  onChange={(e) => onSlackWebhookChange(e.target.value)}
                  placeholder="https://hooks.slack.com/services/…"
                />
              </Field>
            </div>
            <Field>
              <FieldLabel htmlFor="notif-webhook">Webhook URL override</FieldLabel>
              <Input
                id="notif-webhook"
                type="url"
                value={webhookUrl}
                onChange={(e) => onWebhookUrlChange(e.target.value)}
                placeholder="https://example.com/clickweave"
              />
              <p className="text-xs text-[var(--color-mute)]">
                Set to override the workspace-level webhook for this site only.
              </p>
            </Field>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </section>
  );
}

// Unused import shim — keep the module-level `hostOf` / `useMemo`
// available without TS-warning if a future section needs them.
void hostOf;
void useMemo;
